import React, { useState, useEffect, useCallback } from 'react';
import { Search, User, Archive, Package, CheckCircle2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { buscarEmpleadoPorCedula, buscarEmpleadosPorNombre } from '../services/deposito.services';
import { printBluetoothReceipt } from '@/utils/bluetoothPrinter';

const ModalRecoleccion = ({ isOpen, onClose, onSave, idPlanta, plantaNombre }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [searchingEmpleado, setSearchingEmpleado] = useState(false);

  // Búsqueda de empleado
  const [modoBusqueda, setModoBusqueda] = useState('cedula'); // 'cedula' | 'nombre'
  const [cedula, setCedula] = useState('');
  const [busquedaNombre, setBusquedaNombre] = useState('');
  const [resultadosNombre, setResultadosNombre] = useState([]);
  const [empleado, setEmpleado] = useState(null);
  const [empleadoNoEncontrado, setEmpleadoNoEncontrado] = useState(false);

  // Prendas
  const [tiposPrendas, setTiposPrendas] = useState([]);
  const [prendasSeleccionadas, setPrendasSeleccionadas] = useState({});

  // Resumen antes de guardar
  const [showResumen, setShowResumen] = useState(false);

  // Cargar tipos de prenda
  const loadTiposPrendas = useCallback(async () => {
    try {
      const garmentTypesServices = await import('@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services');
      const response = await garmentTypesServices.getAll();
      setTiposPrendas(response?.data || []);
    } catch (error) {
      console.error('Error cargando tipos de prenda:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los tipos de prenda', variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    if (isOpen) loadTiposPrendas();
  }, [isOpen, loadTiposPrendas]);

  // --- Búsqueda de empleado ---

  const buscarEmpleado = async () => {
    if (!cedula.trim()) {
      toast({ title: 'Error', description: 'Ingrese una cédula', variant: 'destructive' });
      return;
    }
    setSearchingEmpleado(true);
    setEmpleadoNoEncontrado(false);
    try {
      const response = await buscarEmpleadoPorCedula(cedula);
      if (response?.data) {
        setEmpleado(response.data);
      } else {
        setEmpleado(null);
        setEmpleadoNoEncontrado(true);
      }
    } catch {
      setEmpleado(null);
      setEmpleadoNoEncontrado(true);
      toast({ title: 'Error', description: 'Empleado no encontrado', variant: 'destructive' });
    } finally {
      setSearchingEmpleado(false);
    }
  };

  const buscarPorNombre = async () => {
    if (!busquedaNombre.trim() || busquedaNombre.trim().length < 2) {
      toast({ title: 'Error', description: 'Ingrese al menos 2 caracteres para buscar', variant: 'destructive' });
      return;
    }
    setSearchingEmpleado(true);
    setEmpleadoNoEncontrado(false);
    setResultadosNombre([]);
    try {
      const response = await buscarEmpleadosPorNombre(busquedaNombre.trim());
      const lista = response?.data || [];
      if (lista.length === 0) setEmpleadoNoEncontrado(true);
      else setResultadosNombre(lista);
    } catch {
      setEmpleadoNoEncontrado(true);
      toast({ title: 'Error', description: 'No se encontraron empleados', variant: 'destructive' });
    } finally {
      setSearchingEmpleado(false);
    }
  };

  const seleccionarEmpleadoDeLista = (emp) => {
    setEmpleado(emp);
    setResultadosNombre([]);
    setBusquedaNombre('');
  };

  // --- Selección de prendas ---

  const togglePrendaSeleccion = (tipoPrenda) => {
    const id = tipoPrenda._id || tipoPrenda.id;
    setPrendasSeleccionadas(prev => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = {
          idPrenda: id,
          nombre: tipoPrenda.nombre,
          descripcion: tipoPrenda.descripcion || '',
          cantidad: 1,
          observaciones: '',
          peso: tipoPrenda.peso || 0,
        };
      }
      return next;
    });
  };

  const actualizarCantidad = (id, value) => {
    const num = Math.max(1, parseInt(value) || 1);
    setPrendasSeleccionadas(prev => ({
      ...prev,
      [id]: { ...prev[id], cantidad: num },
    }));
  };

  const actualizarObservaciones = (id, value) => {
    setPrendasSeleccionadas(prev => ({
      ...prev,
      [id]: { ...prev[id], observaciones: value },
    }));
  };

  // --- Guardar ---

  const handleVerResumen = () => {
    if (!empleado) {
      toast({ title: 'Error', description: 'Debe buscar y seleccionar un empleado', variant: 'destructive' });
      return;
    }
    if (Object.keys(prendasSeleccionadas).length === 0) {
      toast({ title: 'Error', description: 'Debe seleccionar al menos una prenda', variant: 'destructive' });
      return;
    }
    setShowResumen(true);
  };

  const handleConfirmar = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const userName = currentUser.nombre || currentUser.username || currentUser.name || currentUser.email?.split('@')[0] || 'Usuario';
      const userId = currentUser._id || currentUser.id;

      if (!userId) {
        toast({ title: 'Error', description: 'No se pudo identificar el usuario actual', variant: 'destructive' });
        return;
      }

      const prendasArray = Object.values(prendasSeleccionadas);

      const recoleccion = {
        planta: { id: idPlanta, nombre: plantaNombre },
        empleado: {
          id: empleado._id || empleado.id,
          nombre: empleado.nombre,
          apellido: empleado.apellido,
          celular: empleado.celular,
          cedula: empleado.cedula,
          area: empleado.area ? { id: empleado.area.id || empleado.area._id, nombre: empleado.area.nombre } : undefined,
          planta: empleado.planta ? { id: empleado.planta.id || empleado.planta._id, nombre: empleado.planta.nombre } : undefined,
          centroCosto: empleado.centroCosto ? { id: empleado.centroCosto.id || empleado.centroCosto._id, nombre: empleado.centroCosto.nombre } : undefined,
        },
        casillero: 'N/A',
        cantidad_prendas: prendasArray.reduce((sum, p) => sum + (parseInt(p.cantidad) || 1), 0),
        estado: 'recibido',
        creator: { id: userId, nombre: userName },
        prendas: prendasArray.map(p => ({
          idPrenda: p.idPrenda,
          nombre: p.nombre,
          descripcion: p.descripcion,
          cantidad: parseInt(p.cantidad) || 1,
          observaciones: p.observaciones || '',
          peso: parseFloat(p.peso) || 0,
          estado: 'recibido',
        })),
      };

      const savedResponse = await onSave(recoleccion);

      // Intentar imprimir automáticamente por Bluetooth
      try {
        const recoleccionParaImprimir = {
          ...recoleccion,
          ...(savedResponse?.data || {}),
        };
        await printBluetoothReceipt(recoleccionParaImprimir);
        toast({ title: 'Impresión enviada', description: 'Recibo enviado a la impresora Bluetooth.' });
      } catch (printError) {
        console.warn('Impresión Bluetooth no disponible:', printError.message);
      }

      // Limpiar y cerrar
      resetForm();
      setShowResumen(false);
      onClose();

    } catch (error) {
      console.error('Error guardando recolección:', error);
      toast({ title: 'Error', description: 'No se pudo guardar la recolección', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCedula('');
    setBusquedaNombre('');
    setResultadosNombre([]);
    setEmpleado(null);
    setEmpleadoNoEncontrado(false);
    setPrendasSeleccionadas({});
  };

  const handleClose = () => {
    const hayDatos = empleado || Object.keys(prendasSeleccionadas).length > 0;
    if (hayDatos) {
      if (window.confirm('¿Está seguro de cerrar? Se perderán los datos ingresados.')) {
        resetForm();
        onClose();
      }
    } else {
      resetForm();
      onClose();
    }
  };

  const prendasArray = Object.values(prendasSeleccionadas);
  const totalPrendas = prendasArray.reduce((sum, p) => sum + (parseInt(p.cantidad) || 1), 0);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              Nueva Recolección de Prendas
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Búsqueda de Empleado */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-500" />
                  Buscar Empleado
                </h3>
                <div className="flex items-center border rounded-lg overflow-hidden text-sm">
                  <button
                    type="button"
                    onClick={() => { setModoBusqueda('cedula'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                    className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'cedula' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    Por cédula
                  </button>
                  <button
                    type="button"
                    onClick={() => { setModoBusqueda('nombre'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                    className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'nombre' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    Por nombre
                  </button>
                </div>
              </div>

              {modoBusqueda === 'cedula' && (
                <div className="flex gap-3">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="cedula">Cédula *</Label>
                    <Input
                      id="cedula"
                      placeholder="Ingrese la cédula"
                      value={cedula}
                      onChange={(e) => setCedula(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && buscarEmpleado()}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={buscarEmpleado} disabled={searchingEmpleado} className="flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      {searchingEmpleado ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </div>
                </div>
              )}

              {modoBusqueda === 'nombre' && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="busquedaNombre">Nombre o apellido *</Label>
                      <Input
                        id="busquedaNombre"
                        placeholder="Ingrese nombre o apellido"
                        value={busquedaNombre}
                        onChange={(e) => setBusquedaNombre(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && buscarPorNombre()}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={buscarPorNombre} disabled={searchingEmpleado} className="flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        {searchingEmpleado ? 'Buscando...' : 'Buscar'}
                      </Button>
                    </div>
                  </div>

                  {resultadosNombre.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                        {resultadosNombre.length} resultado(s) — seleccione uno
                      </div>
                      <div className="max-h-48 overflow-y-auto divide-y">
                        {resultadosNombre.map((emp) => (
                          <button
                            key={emp._id || emp.id}
                            type="button"
                            onClick={() => seleccionarEmpleadoDeLista(emp)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          >
                            <div>
                              <p className="font-medium text-sm">{emp.nombre} {emp.apellido}</p>
                              <p className="text-xs text-muted-foreground">Cédula: {emp.cedula}</p>
                            </div>
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                              {emp.area?.nombre || 'Sin área'}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {empleadoNoEncontrado && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                    ❌ No se encontró ningún empleado con {modoBusqueda === 'cedula' ? `la cédula "${cedula}"` : `el nombre "${busquedaNombre}"`}
                  </p>
                </div>
              )}

              {empleado && (
                <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className="font-semibold text-green-700 dark:text-green-300">✓ {empleado.nombre} {empleado.apellido}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">Tel: {empleado.celular}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">Área: {empleado.area?.nombre || 'N/A'}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">Planta: {empleado.planta?.nombre || 'N/A'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Selección de Prendas */}
            {empleado && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Archive className="h-4 w-4 text-green-500" />
                    Seleccionar Prendas
                  </h3>
                  {prendasArray.length > 0 && (
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {prendasArray.length} tipo(s) · {totalPrendas} unidad(es) en total
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto p-1">
                  {tiposPrendas.map(tipo => {
                    const id = tipo._id || tipo.id;
                    const isSelected = !!prendasSeleccionadas[id];

                    return (
                      <div
                        key={id}
                        onClick={() => togglePrendaSeleccion(tipo)}
                        className={`border rounded-lg p-3 cursor-pointer select-none transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm leading-tight">{tipo.nombre}</p>
                            {tipo.descripcion && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">{tipo.descripcion}</p>
                            )}

                            {isSelected && (
                              <div
                                className="mt-3 space-y-2 pt-2 border-t border-green-200 dark:border-green-800"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div>
                                  <Label className="text-xs text-muted-foreground">Cantidad</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <button
                                      type="button"
                                      onClick={() => actualizarCantidad(id, (prendasSeleccionadas[id]?.cantidad || 1) - 1)}
                                      className="h-8 w-8 rounded border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                      −
                                    </button>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={prendasSeleccionadas[id]?.cantidad || 1}
                                      onChange={(e) => actualizarCantidad(id, e.target.value)}
                                      className="h-8 text-sm text-center w-16"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => actualizarCantidad(id, (prendasSeleccionadas[id]?.cantidad || 1) + 1)}
                                      className="h-8 w-8 rounded border border-gray-300 flex items-center justify-center text-lg font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">Observaciones</Label>
                                  <Input
                                    placeholder="Opcional"
                                    value={prendasSeleccionadas[id]?.observaciones || ''}
                                    onChange={(e) => actualizarObservaciones(id, e.target.value)}
                                    className="h-8 text-sm mt-1"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleVerResumen}
              disabled={!empleado || prendasArray.length === 0}
              className="min-w-[170px]"
            >
              <Package className="h-4 w-4 mr-2" />
              {prendasArray.length > 0
                ? `Guardar · ${totalPrendas} prenda(s)`
                : 'Guardar Recolección'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de resumen / confirmación */}
      <Dialog open={showResumen} onOpenChange={() => setShowResumen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Confirmar Recolección
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Empleado */}
            {empleado && (
              <div className="rounded-lg border p-4 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Empleado</p>
                <p className="font-semibold">{empleado.nombre} {empleado.apellido}</p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>C.C. {empleado.cedula}</span>
                  {empleado.area?.nombre && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3.5 w-3.5" />
                      {empleado.area.nombre}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Prendas */}
            <div className="rounded-lg border overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b flex items-center justify-between">
                <span>Prendas</span>
                <span>{totalPrendas} unidad(es)</span>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {prendasArray.map((p) => (
                  <div key={p.idPrenda} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{p.nombre}</p>
                      {p.observaciones && (
                        <p className="text-xs text-muted-foreground italic">{p.observaciones}</p>
                      )}
                    </div>
                    <span className="text-sm font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full">
                      × {p.cantidad}
                    </span>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-t flex justify-between text-sm font-semibold">
                <span>Total prendas</span>
                <span>{totalPrendas}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResumen(false)} disabled={loading}>
              Volver a editar
            </Button>
            <Button onClick={handleConfirmar} disabled={loading} className="min-w-[140px]">
              {loading ? 'Guardando...' : 'Confirmar y Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalRecoleccion;
